import { ERROR, SUCCESS } from "../../config/action-states";
import { withCachedResponse } from "../../helpers/cached-request";
import { withExponentialBackoffRetry } from "../../helpers/function";
import { isServerDownError } from "../../helpers/http";
import logger from "../../helpers/logger";
import { parseProductForDisplay, parseProductVariantForDisplay } from "../../helpers/product";
//import useQuery, { parseAsNumber } from "../../hooks/useQuery";
import type { ApiResponse } from "../../interfaces";
import type { Product, ProductVariant } from "../../interfaces/product";
import UserService from "../../services/user-service";
import { message } from "../../strings";
import createStore from "../create-store";
import { useCurrentUser } from "../current-user";

let clearCachedWishlist: () => void;
let clearCachedWishlistCount: () => void;
let cachedGetUserWishlist;
let cachedGetUserWishlistCount;
const getWishlistWithExponentialBackoff = withExponentialBackoffRetry<ApiResponse>(UserService.getUserWishlist);
const getWishlistCountWithExponentialBackoff = withExponentialBackoffRetry<ApiResponse>(UserService.getUserWishlistCount);

const [useWishlist, setWishlist] = createStore<(Product|ProductVariant)[]>([]);
const [useWishlistCount, setWishlistCount] = createStore<number>(0);
const [useIsFetchingWishlist, setIsFetchingWishlist] = createStore<boolean>(false);
const [useIsFetchingWishlistCount, setIsFetchingWishlistCount] = createStore<boolean>(false);
const [useIsAddingItemToWishlist, setIsAddingItemToWishlist] = createStore<Record<string, boolean>>({});
const [useIsRemovingItemFromWishlist, setIsRemovingItemFromWishlist] = createStore<Record<string, boolean>>({});
const [useItemRemovedFromWishlist, setItemRemovedFromWishlist] = createStore<Record<string, boolean>>({});
const [useAddItemToWishlistError, setAddItemToWishlistError] = createStore<Record<string, string|null>>({});
const [useAddItemToWishlistSuccess, setAddItemToWishlistSuccess] = createStore<Record<string, string|null>>({});

const [useRemoveItemFromWishlistError, setRemoveItemFromWishlistError] = createStore<Record<string, string|null>>({});
//const [useWishlistFetched, setWishlistFetched] = createStore(false);

async function handleApiCall(apiCall: Promise<ApiResponse>, method: string): Promise<ApiResponse> {
  const response = await apiCall;

  if(response && SUCCESS in response) {
    const data = response.data;

    // Clear the previously cached wishlist
    // so that when the next fetchWishlist call is made,
    // it sees the updated wishlist info and not the cached (now stale) version.
    clearCachedWishlist?.();

    const parsedWishlist = parseApiWishlistResponse(data?.wishlist ?? []);
    
    setWishlist(parsedWishlist  as unknown as Product[]);
  } else if(response && ERROR in response) {
    logger.error(
      `store::wishlist::${method}: API returned error response:`,
      response.errorData.message
    );
  }

  return response;
}

function parseApiWishlistResponse(wishlist): (Product | ProductVariant)[] {
  return wishlist.map(product => {
    return (
      product.product 
        ? parseProductVariantForDisplay(product, product.product) 
        : parseProductForDisplay(product)
    );
  });
}

export default function useWishlistExported() {
  const wishlist = useWishlist();
  const wishlistCount = useWishlistCount();
  const isFetchingWishlist = useIsFetchingWishlist();
  const isFetchingWishlistCount = useIsFetchingWishlistCount();
  const isAddingItemToWishlist = useIsAddingItemToWishlist();
  const isRemovingItemFromWishlist = useIsRemovingItemFromWishlist();
  const itemRemovedFromWishlist = useItemRemovedFromWishlist();
  const addItemToWishlistError = useAddItemToWishlistError();
  const addItemToWishlistSuccess = useAddItemToWishlistSuccess();
  const removeItemFromWishlistError = useRemoveItemFromWishlistError();
  
  const [currentUser] = useCurrentUser();

  async function addItemToWishlist(item) {
    if(!currentUser) {
      setAddItemToWishlistError(prev => ({ ...prev, [item.id]: message("LOGIN_TO_ADD_PRODUCT_TO_WISHLIST") }));
      return;
    }

    const itemId = item.id;
    const itemType = item.type;
    const userId = currentUser.id;

    setAddItemToWishlistError(prev => ({ ...prev, [itemId]: null }));
    setAddItemToWishlistSuccess(prev => ({ ...prev, [itemId]: null }));
    setIsAddingItemToWishlist(prev => ({ ...prev, [itemId]: true }));

    try {
      const response = await handleApiCall(
        UserService.addItemToUserWishlist(userId, item),
        "addItemToUserWishlist",
      );

      if(response && SUCCESS in response) { 
        clearCachedWishlist?.();
        clearCachedWishlistCount?.();
        setAddItemToWishlistSuccess(prev => ({ 
          ...prev, 
          [itemId]: message("PRODUCT_ADDED_TO_WISHLIST").replace(":productName", "Product"), 
        }));
        fetchWishlistCount(userId);
        fetchWishlist(userId);
      } else if(response && ERROR in response) {
        setAddItemToWishlistError(prev => ({ ...prev, [itemId]: response.errorData.message }));
      }
    } catch(error) {
      logger.error(`Error adding item ${itemId} of type ${itemType} to wishlist`, error);
    } finally {
      setIsAddingItemToWishlist(prev => ({ ...prev, [itemId]: false }));
    }
  }

  async function removeItemFromWishlist(item) {
    if(!currentUser) {
      setRemoveItemFromWishlistError(prev => ({ ...prev, [item.id]: message("LOGIN_TO_REMOVE_PRODUCT_FROM_WISHLIST") }));
      return;
    }

    const itemId = item.id;
    const userId = currentUser.id;

    setIsRemovingItemFromWishlist(prev => ({ ...prev, [itemId]: true }));

    try {
      const response = await UserService.removeItemFromUserWishlist(userId, itemId);

      if(response && SUCCESS in response) {
        clearCachedWishlist?.();
        clearCachedWishlistCount?.();
        setItemRemovedFromWishlist(prev => ({ ...prev, [itemId]: true }));
        fetchWishlistCount(userId);
        //fetchWishlist(userId, { page: queryStringData.page, limit: queryStringData.limit });
        //await handleApiCall(UserService.getUserWishlist(userId), "removeItemFromUserWishlist");
      }
    } catch(error) {
      logger.error(`Error removing item ${itemId} from wishlist`, error);
    } finally {
      setIsRemovingItemFromWishlist(prev => ({ ...prev, [itemId]: false }));
    }
  }

  interface FetchWishlistReturnType {
    total: number;
    pages: number;
    length: number;
    wishlist: Array<Product | ProductVariant>;
  }

  /**
   * For the other functions to work (addItem, removeItem),
   * We must first call the fetchWishlist(userId)
   * so that the userId is set for the other functions.
   */
  async function fetchWishlist(userId: string, params?: { page?: number; limit?: number }): Promise<FetchWishlistReturnType | undefined> {
    setIsFetchingWishlist(true);

    cachedGetUserWishlist = cachedGetUserWishlist ?? withCachedResponse(getWishlistWithExponentialBackoff, { 
      cacheKey: `${userId}_wishlist`, 
      expiresIn: 300,
      isSuccessfulRequest: (response) => { 
        let requestSuccessful = SUCCESS in response;
        const data = response.data as unknown as FetchWishlistReturnType;
        const { page } = params ?? {};

        if(page) {
          requestSuccessful = (requestSuccessful && (page <= data.pages));
        }

        return requestSuccessful;
      },
    });

    ///* @ts-expect-error: ignore TS error about .clearCache */
    clearCachedWishlist = clearCachedWishlist ?? cachedGetUserWishlist.clearCache;

    try {
      const { page = 1, limit = 20 } = params ?? {};
      const response = await cachedGetUserWishlist(userId, { page, limit });

      if(response && SUCCESS in response) {
        const data = response.data as unknown as FetchWishlistReturnType;

        const wishlist = parseApiWishlistResponse(data?.wishlist ?? []);

        //if(autoFetch) { 
          setWishlist(wishlist);
        //}

        return {
          ...data,
          wishlist,
        };
      }
    } catch(error) {
      logger.error(
        `store::wishlist::fetchWishlist:: Error fetching wishlist for user ${userId}`,
        error
      );

      if(isServerDownError(error)) {
        throw error;
      }
    } finally {
      setIsFetchingWishlist(false);
    }
  }

  async function fetchWishlistCount(userId: string): Promise<{ count: number; } | undefined> {
    setIsFetchingWishlistCount(true);
    
    cachedGetUserWishlistCount = cachedGetUserWishlistCount ?? withCachedResponse(getWishlistCountWithExponentialBackoff, { 
      cacheKey: `${userId}_wishlist_count`, 
      expiresIn: 300,
      isSuccessfulRequest: (response) => SUCCESS in response, 
    });

    ///* @ts-expect-error: ignore TS error about .clearCache */
    clearCachedWishlistCount = clearCachedWishlistCount ?? cachedGetUserWishlistCount.clearCache;

    try {
      const response = await cachedGetUserWishlistCount(userId);

      if(response && SUCCESS in response) {
        const data = response.data as unknown as { count: number };
        const count = data?.count ?? 0;

        setWishlistCount(count);

        return {
          count,
        };
      }
    } catch(error) {
      setWishlistCount(wishlistCount);
      logger.error(
        `store::wishlist::fetchWishlistCount:: Error fetching count of items in wishlist for user ${userId}`,
        error
      );

      if(isServerDownError(error)) {
        throw error;
      }
    } finally {
      setIsFetchingWishlistCount(false);
    }
  }

  return {
    wishlist,
    wishlistCount,
    addItemToWishlist,
    removeItemFromWishlist,
    fetchWishlist,
    fetchWishlistCount,

    isAddingItemToWishlist,
    isRemovingItemFromWishlist,
    isFetchingWishlist,
    isFetchingWishlistCount, 

    addItemToWishlistError,
    addItemToWishlistSuccess,
    removeItemFromWishlistError,
    itemRemovedFromWishlist,

    //setAddItemToWishlistError,
    //setAddItemToWishlistSuccess, 
    unsetAddItemToWishlistError: (itemId: string) => setAddItemToWishlistError(prev => ({ ...prev, [itemId]: null })),
    unsetAddItemToWishlistSuccess: (itemId: string) => setAddItemToWishlistSuccess(prev => ({ ...prev, [itemId]: null })),
    unsetRemoveItemFromWishlistError: (itemId: string) => setRemoveItemFromWishlistError(prev => ({ ...prev, [itemId]: null })),
  }
}
